// The two calendar settings that belong to a person rather than to a workspace:
// which calendars they see, and which of their own devices can see this one.
//
// Both live on Profile because both follow the person. A subscription to
// "Holidays in India" is not a property of the agency you happen to be acting
// in — it is a property of somebody who plans Indian campaigns, and it should
// still be there tomorrow when they switch to a client's workspace.

import { useCallback, useEffect, useState } from "react";
import {
  createCalendarShare, listCalendarFeeds, listCalendarShares, listCalendarSubscriptions,
  revokeCalendarShare,
  type CalendarFeed, type CalendarShare, type CalendarSubscription,
} from "../lib/calendar";
import { logError } from "../lib/logger";
import { CalendarPicker } from "./CalendarPicker";
import { Spinner } from "./Spinner";

/** A link that has just been minted, shown once and never retrievable again. */
interface FreshLink {
  id: string;
  label: string;
  url: string;
  webcal: string;
}

export function CalendarSettings({ workspaceId }: { workspaceId: string | null }) {
  const [feeds, setFeeds] = useState<CalendarFeed[]>([]);
  const [subs, setSubs] = useState<CalendarSubscription[]>([]);
  const [shares, setShares] = useState<CalendarShare[]>([]);
  const [fresh, setFresh] = useState<FreshLink | null>(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCalendars = useCallback(async () => {
    const [f, s] = await Promise.all([listCalendarFeeds(), listCalendarSubscriptions()]);
    setFeeds(f);
    setSubs(s);
  }, []);

  const loadShares = useCallback(async () => {
    if (!workspaceId) return;
    setShares(await listCalendarShares(workspaceId));
  }, [workspaceId]);

  useEffect(() => {
    loadCalendars().catch((e) => {
      setError(e.message ?? String(e));
      void logError("calendar_view", e, { level: "warn", step: "load_calendar_settings" });
    });
  }, [loadCalendars]);

  useEffect(() => { loadShares().catch(() => setShares([])); }, [loadShares]);

  async function mint() {
    if (!workspaceId) return;
    setBusy("create");
    setError(null);
    setCopied(false);
    try {
      const link = await createCalendarShare(workspaceId, label.trim() || "My calendar");
      // Held in component state only. The database kept a hash, so this is the
      // one and only moment this URL exists anywhere we can show it.
      setFresh(link);
      setLabel("");
      await loadShares();
    } catch (e: any) {
      setError(e.message ?? String(e));
      void logError("calendar_view", e, { step: "create_calendar_share" });
    } finally {
      setBusy(null);
    }
  }

  async function revoke(id: string) {
    setBusy(`revoke:${id}`);
    setError(null);
    try {
      await revokeCalendarShare(id);
      if (fresh?.id === id) setFresh(null);
      await loadShares();
    } catch (e: any) {
      setError(e.message ?? String(e));
    } finally {
      setBusy(null);
    }
  }

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      // A blocked clipboard is not a failure worth a red box: the URL is on
      // screen and selectable, which is the fallback every browser still has.
      setCopied(false);
    }
  }

  return (
    <>
      <CalendarPicker feeds={feeds} subscriptions={subs} onChanged={loadCalendars} />

      <div className="card">
        <h2>Your calendar, in your calendar</h2>
        <p className="muted" style={{ maxWidth: "62ch" }}>
          Subscribe to your scheduled posts from Apple Calendar, Google Calendar or Outlook and
          they appear beside your meetings, on every device those apps sync to. It is read-only,
          and it carries titles and times — never captions, because the address works without a
          password.
        </p>

        {error && <div className="card err">{error}</div>}

        {fresh && (
          <div className="notice">
            <strong>Copy this now.</strong> It is shown once — we keep only a fingerprint of it,
            so it cannot be shown again. Lost it? Make another and revoke this one.
            <div className="cal-share-url">
              <code>{fresh.webcal}</code>
              <button className="sm" onClick={() => copy(fresh.webcal)}>
                {copied ? "Copied" : "Copy"}
              </button>
              <a className="btn-link sm" href={fresh.webcal}>Open in calendar app</a>
            </div>
            <p className="muted" style={{ marginTop: ".5rem", fontSize: ".8rem" }}>
              Apple Calendar: File → New Calendar Subscription. Google Calendar: Other calendars →
              From URL. Outlook: Add calendar → Subscribe from web.
            </p>
          </div>
        )}

        {shares.length > 0 && (
          <div style={{ marginTop: ".8rem" }}>
            {shares.map((s) => (
              <div key={s.id} className="cal-share-row">
                <span className="cal-share-name">{s.label}</span>
                <span className="muted">
                  added {new Date(s.created_at).toLocaleDateString()}
                  {s.last_used_at
                    ? ` · last read ${new Date(s.last_used_at).toLocaleDateString()}`
                    : " · never read yet"}
                </span>
                <span className="spacer" style={{ flex: 1 }} />
                <button className="sm" disabled={busy !== null} onClick={() => revoke(s.id)}>
                  {busy === `revoke:${s.id}` ? <><Spinner /> Revoking…</> : "Revoke"}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* One link per device, because each is shown once: "add it to my phone
            too" must not mean "break the link on my laptop". */}
        <div className="row" style={{ marginTop: ".8rem" }}>
          <label className="visually-hidden" htmlFor="share-label">Name this link</label>
          <input
            id="share-label"
            value={label}
            maxLength={60}
            placeholder="iPhone, work laptop…"
            onChange={(e) => setLabel(e.target.value)}
            style={{ width: "auto", minWidth: "12rem" }}
          />
          <button className="primary sm" disabled={busy !== null || !workspaceId} onClick={mint}>
            {busy === "create" ? <><Spinner /> Creating…</> : "Create a subscription link"}
          </button>
        </div>
      </div>
    </>
  );
}
